import type { Track } from './trackTypes';
import { updateTrackMetadata } from './trackService';
import { logError, logInfo } from './logtail';

export type KanbanSort = 'created' | 'updated' | 'name';

// Centralized list of statuses used across the app
export const TRACK_STATUS_VALUES: string[] = [
  'Draft',
  'Demo',
  'In Progress',
  'Mixing and Mastering',
  'Complete',
  'Published',
  'Live',
];

export interface GroupedTracksByStatus {
  [status: string]: Track[];
}

export function sortTracks(tracks: Track[], sortBy: KanbanSort): Track[] {
  return [...tracks].sort((a, b) => {
    if (sortBy === 'name') {
      return a.name.localeCompare(b.name);
    } else if (sortBy === 'created') {
      return new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime();
    } else if (sortBy === 'updated') {
      const aUpdated = (a as Track & { lastUpdated?: string }).lastUpdated || a.createdDate;
      const bUpdated = (b as Track & { lastUpdated?: string }).lastUpdated || b.createdDate;
      return new Date(bUpdated).getTime() - new Date(aUpdated).getTime();
    }
    return 0;
  });
}

export function groupTracksByStatus(tracks: Track[], sortBy: KanbanSort): GroupedTracksByStatus {
  const grouped: GroupedTracksByStatus = {};
  for (const status of TRACK_STATUS_VALUES) {
    grouped[status] = [];
  }
  for (const track of tracks) {
    const status = TRACK_STATUS_VALUES.includes(track.status) ? track.status : 'Draft';
    if (!grouped[status]) grouped[status] = [];
    grouped[status].push(track);
  }
  for (const status of Object.keys(grouped)) {
    grouped[status] = sortTracks(grouped[status], sortBy);
  }
  return grouped;
}

export async function changeTrackStatus(trackId: string, newStatus: string): Promise<Track> {
  try {
    const updated = await updateTrackMetadata(trackId, { status: newStatus });
    logInfo('Track status updated via kanban', { trackId, newStatus });
    return updated;
  } catch (error) {
    logError('Failed to update track status via kanban', { trackId, newStatus, error });
    throw error;
  }
}

export function getStatusColor(status: string): { bg: string; text: string; border: string } {
  switch (status) {
    case 'Complete':
      return { bg: 'var(--status-complete-bg)', text: 'var(--status-complete-text)', border: 'var(--status-complete-border)' };
    case 'In Progress':
      return { bg: 'var(--status-progress-bg)', text: 'var(--status-progress-text)', border: 'var(--status-progress-border)' };
    case 'Mixing and Mastering':
      return { bg: 'var(--status-mixing-bg)', text: 'var(--status-mixing-text)', border: 'var(--status-mixing-border)' };
    case 'Draft':
      return { bg: 'var(--status-draft-bg)', text: 'var(--status-draft-text)', border: 'var(--status-draft-border)' };
    case 'Demo':
      return { bg: 'var(--status-demo-bg)', text: 'var(--status-demo-text)', border: 'var(--status-demo-border)' };
    case 'Published':
      return { bg: 'var(--status-published-bg)', text: 'var(--status-published-text)', border: 'var(--status-published-border)' };
    case 'Live':
      return { bg: 'var(--status-live-bg)', text: 'var(--status-live-text)', border: 'var(--status-live-border)' };
    default:
      return { bg: 'var(--status-default-bg)', text: 'var(--status-default-text)', border: 'var(--status-default-border)' };
  }
}


